import { FormFieldError } from '@ipfabric/ui-components/FormFieldError/FormFieldError';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { type UseFormReturn, useWatch } from 'react-hook-form';
import type { CustomDiscoveryDefinitionRow } from './CustomDiscoveryDefinitionRow';
import { useDefinitionCheck } from './useDefinitionCheck';

type Props = {
  form: UseFormReturn<{
    customDiscoveryDefinitions: CustomDiscoveryDefinitionRow[];
  }>;
  index: number;
};

const mutedClassName = 'text-xs text-default text-level-6';

/** The server check of a definition, under its editor. */
export const DefinitionCheckStatus = ({ form, index }: Props) => {
  const yamlPath = `customDiscoveryDefinitions.${index}.yaml` as const;
  const yaml = useWatch({ control: form.control, name: yamlPath }) ?? '';
  // After a save attempt, a new result updates the field error at once.
  const onChecked = useStableCallback(() => {
    if (form.formState.isSubmitted) {
      void form.trigger(yamlPath);
    }
  });
  const check = useDefinitionCheck(yaml, onChecked);

  switch (check.status) {
    case 'checking':
      return <span className={mutedClassName}>Checking the definition…</span>;
    case 'valid':
      return (
        <span className={mutedClassName}>
          The definition loads as “{check.name}”.
        </span>
      );
    case 'invalid':
      return <FormFieldError error={check.error} />;
    case 'unavailable':
      return (
        <span className={mutedClassName}>
          The definition could not be checked now; saving checks it again.
        </span>
      );
    default:
      return null;
  }
};
